import { format } from "date-fns";

/**
 * Normalize a unit-number search query so typing what's actually displayed
 * on screen ("Unit D00-03") still matches the underlying apartment field,
 * which only stores the bare value ("D00-03") without the "Unit " label.
 * Without this, a plain substring search can never match — the query is
 * literally longer than the field it's being compared against.
 */
export const stripUnitPrefix = (query) => (query || "").replace(/^\s*unit\s+/i, "").trim();

/**
 * Extract a plain ID string from a value that might already be a string,
 * or an object with .id/._id (e.g. sale.client_id after backend .populate()).
 * Use this whenever comparing a populate-able reference field against a
 * known ID string with === or .includes(), since the field's shape can be
 * either one depending on whether the query populated it.
 */
export const extractId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.id || value._id || "";
  return String(value);
};

/**
 * Find a client from a client reference, which may be either:
 * - an already-populated object (e.g. sale.client_id after backend .populate()), or
 * - a plain ID string (e.g. reservation.client_id, which is not populated)
 */
export const getClient = (clientRef, clients) => {
  if (clientRef && typeof clientRef === "object") return clientRef;
  return clients.find((c) => c.id === clientRef);
};

/**
 * Find an apartment from an apartment reference, which may be either:
 * - an already-populated object (e.g. sale.apartment_id after backend .populate()), or
 * - a plain ID string (e.g. reservation.apartment_id, which is not populated)
 */
export const getApartment = (apartmentRef, apartments) => {
  if (apartmentRef && typeof apartmentRef === "object") return apartmentRef;
  return apartments.find((a) => a.id === apartmentRef);
};

/**
 * Calculate the remaining balance on a sale.
 */
export const calculateRemainingBalance = (sale) => {
  return (sale.total_price || 0) - (sale.total_paid || 0);
};

/**
 * Determine whether a sale has overdue payments.
 * Returns { isOverdue, missedPayments, amountOverdue } or { isOverdue: false }.
 */
export const checkOverduePayments = (sale) => {
  if (sale.status === "completed") return { isOverdue: false };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let expectedPaid = sale.first_installment || 0;

  if (sale.monthly_payment && sale.payment_duration_months && sale.sale_date) {
    const paymentFrequency = sale.payment_frequency_months || 1;
    const saleDate = new Date(sale.sale_date);
    saleDate.setHours(0, 0, 0, 0);

    const firstPaymentDate = new Date(saleDate);
    firstPaymentDate.setMonth(firstPaymentDate.getMonth() + paymentFrequency);

    const paymentEndDate = new Date(firstPaymentDate);
    paymentEndDate.setMonth(paymentEndDate.getMonth() + sale.payment_duration_months);

    let currentDate = new Date(firstPaymentDate);
    while (currentDate < today && currentDate <= paymentEndDate) {
      expectedPaid += sale.monthly_payment;
      currentDate.setMonth(currentDate.getMonth() + paymentFrequency);
    }
  }

  const actualPaid = sale.total_paid || 0;

  if (actualPaid < expectedPaid) {
    const amountOverdue = expectedPaid - actualPaid;
    // Math.round prevents floating-point truncation (e.g. 1.99 -> 2)
    const missedPayments =
      sale.monthly_payment > 0
        ? Math.round(amountOverdue / sale.monthly_payment)
        : 0;
    return { isOverdue: true, missedPayments, amountOverdue };
  }

  return { isOverdue: false };
};

/**
 * Filter an array of items by a date range object.
 * dateRange = { type: 'all'|'thisWeek'|'lastWeek'|'thisMonth'|'lastMonth'|'custom', startDate, endDate }
 */
export const filterByDateRange = (items, dateKey, dateRange) => {
  if (dateRange.type === "all") return items;

  let start = new Date();
  let end = new Date();

  switch (dateRange.type) {
    case "thisWeek":
      start = new Date();
      start.setDate(start.getDate() - start.getDay());
      start.setHours(0, 0, 0, 0);
      break;
    case "lastWeek":
      start = new Date();
      start.setDate(start.getDate() - start.getDay() - 7);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    case "thisMonth":
      start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    case "lastMonth":
      start = new Date();
      start.setDate(1);
      start.setMonth(start.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(1);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;
    case "custom":
      if (dateRange.startDate) {
        start = new Date(dateRange.startDate);
        start.setHours(0, 0, 0, 0);
      }
      if (dateRange.endDate) {
        end = new Date(dateRange.endDate);
        end.setHours(23, 59, 59, 999);
      }
      break;
    default:
      return items;
  }

  return items.filter((item) => {
    const itemDate = new Date(item[dateKey]);
    if (isNaN(itemDate.getTime())) return true;
    return itemDate >= start && itemDate <= end;
  });
};

/**
 * Human-readable label for the sales date filter.
 */
export const getSalesFilterLabel = (salesDateRange) => {
  switch (salesDateRange.type) {
    case "all":
      return "All Time";
    case "thisWeek":
      return "This Week";
    case "lastWeek":
      return "Last Week";
    case "thisMonth":
      return "This Month";
    case "lastMonth":
      return "Last Month";
    case "custom":
      if (salesDateRange.startDate && salesDateRange.endDate) {
        return `${format(new Date(salesDateRange.startDate), "MMM d")} - ${format(
          new Date(salesDateRange.endDate),
          "MMM d, yyyy"
        )}`;
      }
      return "Custom Range";
    default:
      return "All Time";
  }
};

/**
 * Human-readable label for the payments date filter.
 */
export const getPaymentsFilterLabel = (paymentsDateRange) => {
  switch (paymentsDateRange.type) {
    case "all":
      return "All Time";
    case "thisWeek":
      return "This Week";
    case "lastWeek":
      return "Last Week";
    case "thisMonth":
      return "This Month";
    case "lastMonth":
      return "Last Month";
    case "custom":
      if (paymentsDateRange.startDate && paymentsDateRange.endDate) {
        return `${format(
          new Date(paymentsDateRange.startDate),
          "MMM d"
        )} - ${format(new Date(paymentsDateRange.endDate), "MMM d, yyyy")}`;
      }
      return "Custom Range";
    default:
      return "All Time";
  }
};